'use client';

import { useTranslations } from 'next-intl';
import { useParams } from 'next/navigation';
import Link from 'next/link';
import BaseAuctionCard from './BaseAuctionCard';

interface CompletedBidCardProps {
  id: string;
  slug: string;
  title: string;
  location: string;
  finalPrice: number;
  myBid: number;
  result: string;
  image: string;
  endDate: string;
  isFavorite?: boolean;
  onFavoriteToggle?: (id: string) => void;
  isAuthenticated?: boolean; // Server-side'dan gelecek
}

export default function CompletedBidCard({
  id,
  slug,
  title,
  location,
  finalPrice,
  myBid,
  result,
  image,
  endDate,
  isFavorite = false,
  onFavoriteToggle,
  isAuthenticated = false
}: CompletedBidCardProps) {
  const t = useTranslations('profile');
  const params = useParams();

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };


  const favoriteButton = isAuthenticated && onFavoriteToggle ? (
    <button
      onClick={() => onFavoriteToggle(id)}
      className="absolute top-4 right-4 w-10 h-10 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white transition-colors"
    >
      <svg 
        className={`w-5 h-5 ${isFavorite ? 'text-red-500 fill-current' : 'text-gray-600'}`} 
        fill={isFavorite ? 'currentColor' : 'none'} 
        stroke="currentColor" 
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
      </svg>
    </button>
  ) : null;

  const imageOverlay = (
    <>
      <div className="absolute inset-0 bg-black/50"></div>
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="border-4 border-white rounded-lg p-6 transform -rotate-12">
          <p className="text-white text-2xl font-bold font-serif">{t('bids.completed.sold')}</p>
        </div>
      </div>
    </>
  );

  const content = (
    <div className="space-y-3 mb-4">
      <div className="flex justify-between items-center">
        <span className="text-gray-600 text-sm">{t('bids.completed.finalPrice')}</span>
        <span className="text-2xl font-bold text-gray-900">
          {formatPrice(finalPrice)}
        </span>
      </div>
      <div className="flex justify-between items-center">
        <span className="text-gray-600 text-sm">{t('bids.completed.myBid')}</span>
        <span className="text-2xl font-bold text-gray-900">
          {formatPrice(myBid)}
        </span>
      </div>
      <div className="flex justify-between items-center">
        <span className="text-gray-600 text-sm">{t('bids.completed.result')}</span>
        <span className={`text-2xl font-bold ${result.includes('Kazandınız') || result.includes('Won') ? 'text-green-600' : 'text-red-600'}`}>
          {result}
        </span>
      </div>
      <div className="flex justify-between items-center">
        <span className="text-gray-600 text-sm">{t('bids.completed.endDate')}</span>
        <span className="text-2xl font-bold text-gray-900">{endDate}</span>
      </div>
    </div>
  );

  const actionButton = (
    <div className="mt-auto">
      <Link href={`/${params.locale}/muzayedeler/${slug}`}>
        <button className="w-full py-3 px-4 rounded-lg font-bold text-red-600 bg-white border-2 border-red-600 hover:bg-red-50 transition-colors">
          {t('bids.completed.viewDetails')}
        </button>
      </Link>
    </div>
  );

  return (
    <BaseAuctionCard
      id={id}
      title={title}
      location={location}
      image={image}
      isCompleted={true}
      imageOverlay={imageOverlay}
      favoriteButton={favoriteButton}
      actionButton={actionButton}
    >
      {content}
    </BaseAuctionCard>
  );
}
